import { FormGroup, Col } from 'react-bootstrap';
import { BodyLarge, BodySmall, Checkbox, SubtleButton, theme } from '@arnold/common';
import { Trans, useTranslation } from 'react-i18next';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Card, FormGroupLabel, FormRadioInput } from '../Common';
import TeamSelect from '../SurveyGroup/TeamSelect';
import {
  Features,
  SurveyQuery,
  UserQuery,
  ContactType,
  ActiveRespondentsQuery,
  useLogFeEventMutation,
  FeEventType,
} from '../../generated/hooks';
import { SurveyRespondentsTable } from './SurveyParticipantsTable';
import { Modal } from '@arnold/common/lib/components/Modal/Modal';
import styled from 'styled-components';
import { Textarea, FormError } from '../FormComponents';

interface IProps<T> {
  notStarted: boolean;
  setFormValues: Dispatch<SetStateAction<T>>;
  formValues: T;
  activeRespondents?: NonNullable<ActiveRespondentsQuery['user']['organization']>['activeRespondents'];
  teams?: NonNullable<ActiveRespondentsQuery['user']['organization']>['teams'];
  user: UserQuery['user'];
  surveyId?: number;
}

interface MandatoryFormData {
  respondents: NonNullable<SurveyQuery['survey']>['whitelistedRespondents'];
  whitelist: boolean;
  sendEmailSms: boolean;
}

const SurveyParticipantsInput = <T extends MandatoryFormData>({
  notStarted,
  setFormValues,
  formValues,
  activeRespondents = [],
  teams,
  user,
  surveyId,
}: IProps<T>) => {
  const { t } = useTranslation('surveyForm');

  const [logFeEvent] = useLogFeEventMutation();

  const { organization } = user;

  const [showAddParticipantsModal, setShowAddParticipantsModal] = useState(false);

  const [rawInput, setRawInput] = useState('');

  const [unmatchedContactsState, setUnmatchedContactsState] = useState<string[]>([]);

  const activeRespondentContactsSet = useMemo(
    () =>
      new Set(
        activeRespondents.flatMap(
          (respondent) =>
            respondent.contacts?.flatMap((contact) =>
              contact.type === ContactType.Sms
                ? [
                    contact.value?.toLowerCase().replace(/\s+/g, ''),
                    `+${contact.phoneNumber?.nationalNumber?.toLowerCase().replace(/\s+/g, '')}`,
                  ]
                : contact.value?.toLowerCase().replace(/\s+/g, ''),
            ) ?? [],
        ),
      ),
    [activeRespondents],
  );

  const inputContacts = rawInput
    .split(/[,;\n\t]+/)
    .map((c) => c.trim().toLowerCase().replace(/\s+/g, ''))
    .filter((c) => c.length > 0)
    .map((c) => {
      if (c.includes('@') || c.startsWith('+')) {
        return c;
      }
      return `+${c}`;
    });

  const setRespondents = (
    respondents: NonNullable<ActiveRespondentsQuery['user']['organization']>['activeRespondents'],
  ) => {
    const respondentsIds = respondents.map((respondent) => respondent.id);
    setFormValues((oldValues) => ({
      ...oldValues,
      whitelist: true,
      respondents: activeRespondents.filter((activeRespondent) => respondentsIds.includes(activeRespondent.id)),
    }));
  };

  const removeRespondent = (ids: string[]) => {
    setFormValues((oldValues) => ({
      ...oldValues,
      respondents: oldValues.respondents.filter((respondent) => !ids.includes(respondent.id)),
    }));
  };

  const handleBulkAddClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setShowAddParticipantsModal(true);
  };

  return (
    <Card data-icom={'form-survey-participants'}>
      <FormGroupLabel>{t('whitelistSwitch')}</FormGroupLabel>
      <FormGroup className="form-check-group" data-icom={'form-survey-whitelist'}>
        <FormRadioInput
          name="whitelist"
          dataCy="admin-edit-survey-wholeOrganization"
          disabled={!notStarted}
          value={false}
          clicked={() => {
            setFormValues((oldValues) => ({
              ...oldValues,
              respondents: [],
              whitelist: false,
            }));
          }}
          defaultChecked={!formValues.whitelist}
          label={t('wholeOrganization')}
        />
        <FormRadioInput
          name="whitelist"
          dataCy="admin-edit-survey-selectParticipants"
          disabled={!notStarted}
          value={true}
          clicked={() => {
            setFormValues((oldValues) => ({
              ...oldValues,
              whitelist: true,
            }));
          }}
          defaultChecked={formValues.whitelist}
          label={t('selectedRespondents')}
        />
      </FormGroup>
      {formValues.whitelist && (
        <>
          {notStarted && (
            <div className="d-flex" data-cy="admin-edit-survey-selectNewParticipants">
              <TeamSelect
                setRespondents={setRespondents}
                respondents={formValues.respondents}
                activeRespondents={activeRespondents}
                teams={teams}
              />
              <div data-icom={'btn-bulk-add-respondents-in-survey'}>
                <SubtleButton onClick={handleBulkAddClick} style={{ marginLeft: theme.spacing.e }} type="button">
                  {t('addParticipantsModal:ButtonBulkAdd')}
                </SubtleButton>
              </div>
            </div>
          )}
          <Modal
            show={showAddParticipantsModal}
            onHide={() => {
              setShowAddParticipantsModal(false);
              setRawInput('');
              setUnmatchedContactsState([]);
            }}
            onSubmit={() => {
              const matchedRespondents = activeRespondents.filter((respondent) => {
                if (!respondent.contacts) return false;

                return respondent.contacts.some((contact) => {
                  if (contact.type === ContactType.Sms) {
                    const contactValueNationalNumber = `+${contact.phoneNumber?.nationalNumber
                      ?.toLowerCase()
                      .replace(/\s+/g, '')}`;
                    if (inputContacts.includes(contactValueNationalNumber)) {
                      return true;
                    }
                  }
                  const contactValue = contact.value.toLowerCase().replace(/\s+/g, '');
                  return inputContacts.includes(contactValue);
                });
              });

              if (matchedRespondents.length !== inputContacts.length) {
                console.error(
                  'Error: something went wrong, contact support, matched respondents: ' +
                    matchedRespondents.length +
                    ' input contacts: ' +
                    inputContacts.length,
                );
              }

              setFormValues((oldValues) => ({
                ...oldValues,
                whitelist: true,
                respondents: Array.from(new Set([...oldValues.respondents, ...matchedRespondents])),
              }));

              try {
                logFeEvent({
                  variables: {
                    eventType: FeEventType.BulkAddRespondentsInSurvey,
                    payload: {
                      organizationId: organization?.id,
                      surveyId: surveyId?.toString(),
                      originatorId: user.id,
                      detail: `Raw input: ${rawInput}
                        Unmatched contacts: ${unmatchedContactsState.join(', ')}`,
                    },
                  },
                });
              } catch (err) {
                // Do nothing
              }

              setShowAddParticipantsModal(false);
              setRawInput('');
              setUnmatchedContactsState([]);
            }}
            title={t('addParticipantsModal:Title')}
            text={
              <UlDescription>
                <li>
                  <Trans i18nKey={'addParticipantsModal:Description'} components={[<strong />]} />
                </li>
                <li>{t('addParticipantsModal:Description2')}</li>
              </UlDescription>
            }
            content={
              <div className="d-flex flex-column gap-2">
                <InputLabel>{t('addParticipantsModal:Label')}</InputLabel>
                <Textarea
                  placeholder={t('addParticipantsModal:InputPlaceHolder')}
                  minRows={3}
                  maxRows={10}
                  isInvalid={unmatchedContactsState.length > 0}
                  onChange={(e) => {
                    const input = e.target.value;
                    setRawInput(input);

                    const originalInputContacts = input.split(/[,;\n\t]+/);

                    const normalizedInputContacts = originalInputContacts
                      .map((c) => c.trim().toLowerCase().replace(/\s+/g, ''))
                      .map((c) => {
                        if (c.includes('@') || c.startsWith('+') || c.length === 0) {
                          return c;
                        }
                        return `+${c}`;
                      });

                    const unmatchedOriginals: string[] = [];

                    normalizedInputContacts.forEach((contact, i) => {
                      if (contact.length > 0 && !activeRespondentContactsSet.has(contact)) {
                        unmatchedOriginals.push(originalInputContacts[i]);
                      }
                    });

                    setUnmatchedContactsState(unmatchedOriginals);
                  }}
                />

                {unmatchedContactsState.length > 0 && (
                  <FormError>
                    <Trans
                      i18nKey={
                        unmatchedContactsState.length === 1
                          ? 'addParticipantsModal:UnmatchedContactsWarning'
                          : 'addParticipantsModal:UnmatchedContactsWarningPlural'
                      }
                      values={{
                        contacts: unmatchedContactsState.join(', '),
                      }}
                      components={[<strong />]}
                    />
                  </FormError>
                )}
              </div>
            }
            buttons={{
              submit: { title: t('addParticipantsModal:Submit'), disabled: unmatchedContactsState.length > 0 },
              cancel: { title: t('addParticipantsModal:Cancel') },
            }}
          />
          <FormGroup>
            <SurveyRespondentsTable respondents={formValues.respondents} removeRespondent={removeRespondent} />
          </FormGroup>
        </>
      )}
      <BodyLarge margin={`${theme.spacing.f} 0 0 ${theme.spacing.f}`} bold={true}>
        {t('respondentsCount', {
          count: formValues.whitelist ? formValues.respondents.length : activeRespondents.length,
        })}
      </BodyLarge>
      {organization?.featureNames?.find((feature) => feature === Features.SendEmailSms) && (
        <FormGroup as={Col} sm={8} style={{ marginTop: `${theme.spacing.g}` }}>
          <Checkbox
            selected={formValues.sendEmailSms}
            onToggle={() => {
              setFormValues((oldValues) => ({
                ...oldValues,
                sendEmailSms: !oldValues.sendEmailSms,
              }));
            }}
            text={
              <div>
                <div data-icom={'admin-send-email-sms-info'}>{t('sendEmailSms')}</div>
                <BodySmall color={theme.colors.text.secondary}>
                  {t('numberOfAffectedParticipants', {
                    count: formValues.whitelist
                      ? formValues.respondents.filter(
                          (respondent) =>
                            respondent.contacts?.find((contact) => contact.type === ContactType.Email) &&
                            respondent.contacts?.find((contact) => contact.type === ContactType.Sms),
                        ).length
                      : activeRespondents.filter(
                          (respondent) =>
                            respondent.contacts?.find((contact) => contact.type === ContactType.Email) &&
                            respondent.contacts?.find((contact) => contact.type === ContactType.Sms),
                        ).length,
                  })}
                </BodySmall>
              </div>
            }
          />
        </FormGroup>
      )}
    </Card>
  );
};

const UlDescription = styled.ul`
  padding-left: ${theme.spacing.g};
  padding-bottom: ${theme.spacing.g};
`;

const InputLabel = styled.label({
  ...theme.typography.body.small.italic,
  color: theme.colors.text.secondary,
  marginLeft: theme.spacing.f,
});

export default SurveyParticipantsInput;
